import React, { useContext, useState, useCallback } from "react";
import ScreenListItem from "./ScreenListItem";
import { guidGenerator } from "../utils/commonFunctions";
import { MachineContext } from "../fsm/useMachine";
import { FlowObjects, SET_INDEX, REORDER_SCREENS, ReorderKind } from "../fsm/types";
import Screens from "../../model/Screens";

/**
 * Screen Selector - horizontal thumbnail strip at bottom of editor
 *
 * Features:
 * - Click to select screen
 * - Right-click for context menu (duplicate/delete)
 * - Drag to reorder screens
 * - Selected screen highlighted with Flowboard blue
 */
export const ScreenSelector = () => {
    const [current, send] = useContext(MachineContext);
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const screens = current.context.flow.screens;
    const selectedIndex = current.context.index;
    const viewSize = current.context.flow.viewSize;

    // Calculate thumbnail dimensions based on aspect ratio
    // Bigger thumbnails for better UX - easy to see and select
    const thumbHeight = 100;
    const aspectRatio = viewSize.width / viewSize.height;
    const thumbWidth = Math.round(thumbHeight * aspectRatio);

    /**
     * Select a screen
     */
    const handleSelect = useCallback((index: number) => {
        send({ type: SET_INDEX, index });
    }, [send]);

    /**
     * Delete a screen
     */
    const handleDelete = useCallback((index: number) => {
        if (screens.length <= 1) {
            alert("Cannot delete the only screen.");
            return;
        }
        const newScreens = [...screens];
        newScreens.splice(index, 1);
        send({ type: REORDER_SCREENS, screens: newScreens, kind: ReorderKind.Delete });
    }, [screens, send]);

    /**
     * Copy/duplicate a screen
     */
    const handleCopy = useCallback((index: number) => {
        const newScreens = [...screens];
        const toBeCopied = newScreens[index].clone();
        toBeCopied.id = guidGenerator();
        toBeCopied.mixedMediaObjects.forEach((mmo: FlowObjects) => {
            mmo.id = guidGenerator();
        });
        // Insert copy after the original
        newScreens.splice(index + 1, 0, toBeCopied);
        send({ type: REORDER_SCREENS, screens: newScreens, newIndex: index + 1, kind: ReorderKind.Copy });
    }, [screens, send]);

    /**
     * Start dragging a screen
     */
    const handleDragStart = useCallback((index: number) => {
        setDragIndex(index);
    }, []);

    /**
     * Dragging over a screen
     */
    const handleDragOver = useCallback((index: number) => {
        if (dragIndex !== null && dragIndex !== index) {
            setDragOverIndex(index);
        }
    }, [dragIndex]);

    /**
     * End drag - reorder if needed
     */
    const handleDragEnd = useCallback(() => {
        if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
            const newScreens = [...screens];
            const [removed] = newScreens.splice(dragIndex, 1);
            newScreens.splice(dragOverIndex, 0, removed);

            // Calculate new selected index
            let newSelectedIndex = selectedIndex;
            if (dragIndex === selectedIndex) {
                // Dragged the selected screen
                newSelectedIndex = dragOverIndex;
            } else if (dragIndex < selectedIndex && dragOverIndex >= selectedIndex) {
                // Screen moved from before to after selected
                newSelectedIndex = selectedIndex - 1;
            } else if (dragIndex > selectedIndex && dragOverIndex <= selectedIndex) {
                // Screen moved from after to before selected
                newSelectedIndex = selectedIndex + 1;
            }

            send({
                type: REORDER_SCREENS,
                screens: newScreens,
                newIndex: newSelectedIndex,
                kind: ReorderKind.Drag
            });
        }
        setDragIndex(null);
        setDragOverIndex(null);
    }, [dragIndex, dragOverIndex, screens, selectedIndex, send]);

    return (
        <div
            className="screen-selector"
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                padding: '4px 8px',
                height: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                flexWrap: 'nowrap',
                minWidth: 0,
            }}
        >
            {screens.map((screen: Screens, index: number) => (
                <ScreenListItem
                    key={screen.id}
                    item={screen}
                    index={index}
                    isSelected={index === selectedIndex}
                    onSelect={handleSelect}
                    onDelete={handleDelete}
                    onCopy={handleCopy}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    isDragging={dragIndex !== null}
                    dragOverIndex={dragOverIndex}
                    thumbWidth={thumbWidth}
                    thumbHeight={thumbHeight}
                />
            ))}
        </div>
    );
};

export default ScreenSelector;
